import React, { useCallback, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  RadialLinearScale 
} from 'chart.js'
import { Pie, Bar, Line, Doughnut, PolarArea } from 'react-chartjs-2'
import Loading from 'components/spinner/Loading'
import { useLazyGetDashboardStatisticsQuery } from 'app/services/dashboard.service'
import { DASHBOARD, TITLES } from 'locales/locales'

// Dashboard data tiplerini tanımla
interface DashboardBodyItem {
  title: string
  value: number | string
}

interface DashboardItem {
  key: string
  value: number
  description: string
  body: DashboardBodyItem[]
}

type DashboardData = DashboardItem[]

ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement, 
  PointElement, 
  RadialLinearScale
)

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation()
  const [fetchDashboardData] = useLazyGetDashboardStatisticsQuery()
  const [dashboardData, setDashboardData] = useState<DashboardData>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      Loading.ref.show()
      const data = await fetchDashboardData({})
      setDashboardData(data.data.response)
    } catch(e) {
//      console.warn('ERROR', e);
    } finally {
      Loading.ref.hide()
    }
  }

  // Genel görünüm chart'ı
  const overviewChartData = useMemo(() => {
    if (!dashboardData || dashboardData.length === 0) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      }
    }

    return {
      labels: dashboardData.map(item => `${item.description} (${item.value})`),
      datasets: [
        {
          data: dashboardData.map(item => item.value),
          backgroundColor: [
            '#FF6384',
            '#36A2EB', 
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40'
          ],
          borderWidth: 2,
          borderColor: '#fff'
        }
      ]
    }
  }, [dashboardData])

  const newsfeedChartData = useMemo(() => {
    const newsfeedData = dashboardData.find(item => item.key === 'newsfeed')
    if (!newsfeedData) {
      return {
        labels: [],
        datasets: [
          {
            label: '',
            data: [],
            backgroundColor: [],
            borderWidth: 1,
          },
        ],
      }
    }

    return {
      labels: newsfeedData.body.map(item => item.title),
      datasets: [{
        label: newsfeedData.description,
        data: newsfeedData.body.map(item => item.value),
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
        borderWidth: 1,
        borderColor: ['#FF6384', '#36A2EB', '#FFCE56'],
      }]
    }
  }, [dashboardData])

  const channelChartData = useMemo(() => {
    const channelData = dashboardData.find((item) => item.key === 'channels')
    if (!channelData) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 2,
          },
        ],
      }
    }
    const data = [...channelData.body]
    const totalChannelCount = channelData.value
    const totalMessageCount = Number(channelData.body[1].value) || 0
    const totalFileCount = Number(channelData.body[2].value) || 0
    const avarageMessageCountPerUser = (totalMessageCount / totalChannelCount || 0).toFixed(0)
    const avarageFileCountPerUser = (totalFileCount / totalChannelCount || 0).toFixed(0)
    data.push({
      title: t(DASHBOARD.AVARAGE_MESSAGE_COUNT_PER_USER),
      value: avarageMessageCountPerUser,
    })
    data.push({
      title: t(DASHBOARD.AVARAGE_FILE_COUNT_PER_USER),
      value: avarageFileCountPerUser,
    })

    return {
      labels: data.map((item) => `${item.title} (${item.value})`),
      datasets: [
        {
          data: data.map((item) => item.value),
          backgroundColor: ['#4BC0C0', '#9966FF', '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56'],
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    }
  }, [dashboardData])

  // 1-1 Mesaj kategorisi chart'ı (Line Chart)
  const oneToOneChartData = useMemo(() => {
    const messageData = dashboardData.find(item => item.key === 'message')
    if (!messageData) {
      return {
        labels: [],
        datasets: [{
          label: '',
          data: [],
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: '#4BC0C0'
        }]
      }
    }

    return {
      labels: messageData.body.map(item => item.title),
      datasets: [{
        label: messageData.description,
        data: messageData.body.map(item => item.value),
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: '#4BC0C0',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    }
  }, [dashboardData])

  // Dosya kategorisi chart'ı (Polar Area)
  const fileChartData = useMemo(() => {
    const documentData = dashboardData.find(item => item.key === 'document')
    if (!documentData) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderWidth: 2
        }]
      }
    }

    return {
      labels: documentData.body.map(item => item.title),
      datasets: [{
        data: documentData.body.map(item => {
          // Size verisini MB olarak parse etmeye çalış
          if (typeof item.value === 'string' && item.value.includes('MB')) {
            return parseFloat(item.value.replace(' MB', ''))
          }
          return item.value
        }),
        backgroundColor: ['rgba(153, 102, 255, 0.6)', 'rgba(255, 99, 132, 0.6)'],
        borderWidth: 2,
        borderColor: ['#9966FF', '#FF6384']
      }]
    }
  }, [dashboardData])

  // Süre kategorisi chart'ı (Radar Chart)
  const durationChartData = useMemo(() => {
    const videoData = dashboardData.find(item => item.key === 'video')
    if (!videoData) {
      return {
        labels: [],
        datasets: [{
          label: '',
          data: [],
          backgroundColor: 'rgba(255, 206, 86, 0.2)',
          borderColor: '#FFCE56'
        }]
      }
    }

  const totalDurationItem = videoData.body[0]
  const totalParticipantItem = videoData.body[1]

  let avgDurationPerUser = 0
  if (totalDurationItem && totalParticipantItem && Number(totalParticipantItem.value) > 0) {
    avgDurationPerUser = Number(totalDurationItem.value) / Number(totalParticipantItem.value)
  }

  const labels = videoData.body.map((item) => `${item.title} (${item.value})`)
  if (avgDurationPerUser > 0) {
    labels.push(`${t(DASHBOARD.AVARAGE_DURATION_PER_USER)} (${avgDurationPerUser.toFixed(2)})`)
  }

  const data = videoData.body.map((item) => item.value)
  if (avgDurationPerUser > 0) {
    data.push(Number(avgDurationPerUser.toFixed(2)))
  }

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#8BC34A',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  }

  }, [dashboardData])


  const platformChartData = useMemo(() => {
    const userData = dashboardData.find(item => item.key === 'users')
  
    if (!userData) {
      return {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 2,
            borderColor: '#fff',
          },
        ],
      }
    }
  
    const platformItems = userData?.body?.slice(1);
  
    return {
      labels: platformItems.map(item => `${item.title} (${item.value})`),
      datasets: [
        {
          data: platformItems.map(item => Number(item.value) || 0),
          backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0'],
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    }
  }, [dashboardData])

  const createChartOptions = useCallback(() => {
    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom' as const,
          align: 'center' as const,
          title: {
            display: true,
          },
          labels: {
            boxWidth: 20,
            boxHeight: 10,
          },
        },
      },
    }
  }, []);

      return (
        <div>
          <ContentCard>
            <ContentCardHeader>{t(TITLES.DASHBOARD_TITLE)}</ContentCardHeader>
            <ContentCardBody>
              <ChartGrid>
                <ChartItem>
                  <ChartContainer>
                    <Pie data={overviewChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <Doughnut data={platformChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <Bar data={newsfeedChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <Doughnut data={channelChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <Line data={oneToOneChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <PolarArea data={fileChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
                <ChartItem>
                  <ChartContainer>
                    <Pie data={durationChartData} options={createChartOptions()} />
                  </ChartContainer>
                </ChartItem>
              </ChartGrid>
            </ContentCardBody>
          </ContentCard>
        </div>
      )
}

export default AdminDashboard

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`

const ChartContainer = styled.div`
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
`

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-top: 20px;
`

const ChartItem = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
  border: 1px solid #e3ebf6;
`
